import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      }
    ]
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/user',
    name: 'Authorization',
    meta: {
      title: t('router.authorization'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'adminLog',
        component: () => import('@/views/Authorization/AdminLog/AdminLog.vue'),
        name: 'AdminLog',
        meta: {
          title: t('router.adminLog')
        }
      },
      {
        path: 'admin',
        component: () => import('@/views/Authorization/Admin/Admin.vue'),
        name: 'Admin',
        meta: {
          title: t('router.admin')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/Menu.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
          // noCache: true
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/Role.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      }
    ]
  },
  {
    path: '/blogger',
    component: Layout,
    redirect: '/blogger/user',
    name: 'blogger',
    meta: {
      title: t('router.bloggerManager'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'user',
        component: () => import('@/views/Blogger/User/User.vue'),
        name: 'User',
        meta: {
          title: t('router.bloggerList')
        }
      }
    ]
  },
  {
    path: '/settlein',
    component: Layout,
    redirect: '/settlein/pendingreview',
    name: 'SettleIn',
    meta: {
      title: t('router.settleInManage'),
      icon: 'vi-eos-icons:role-binding'
    },
    children: [
      {
        path: 'pendingreview',
        component: () => import('@/views/SettleIn/PendingReview/PendingReview.vue'),
        name: 'SettleInPendingReview',
        meta: {
          title: t('router.pendingReview')
        }
      },
      {
        path: 'rejected',
        component: () => import('@/views/SettleIn/Rejected/Rejected.vue'),
        name: 'SettleInRejected',
        meta: {
          title: t('router.rejected')
        }
      }
    ]
  },
  {
    path: '/article',
    component: Layout,
    redirect: '/article/list',
    name: 'Article',
    meta: {
      title: t('router.articleManage'),
      icon: 'vi-eos-icons:role-binding'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Article/List/List.vue'),
        name: 'ArticleList',
        meta: {
          title: t('router.articleList')
        }
      },
      {
        path: 'pendingreview',
        component: () => import('@/views/Article/PendingReview/PendingReview.vue'),
        name: 'ArticlePendingReview',
        meta: {
          title: t('router.pendingReview')
        }
      },
      {
        path: 'rejected',
        component: () => import('@/views/Article/Rejected/Rejected.vue'),
        name: 'ArticleRejected',
        meta: {
          title: t('router.rejected')
        }
      }
    ]
  },
  {
    path: '/comment',
    component: Layout,
    redirect: '/comment/list',
    name: 'Comment',
    meta: {
      title: t('router.commentManage'),
      icon: 'vi-eos-icons:role-binding'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Comment/List/List.vue'),
        name: 'CommentList',
        meta: {
          title: t('router.commentList')
        }
      },
      {
        path: 'pendingreview',
        component: () => import('@/views/Comment/PendingReview/PendingReview.vue'),
        name: 'CommentPendingReview',
        meta: {
          title: t('router.pendingReview')
        }
      },
      {
        path: 'rejected',
        component: () => import('@/views/Comment/Rejected/Rejected.vue'),
        name: 'CommentRejected',
        meta: {
          title: t('router.rejected')
        }
      }
    ]
  },
  {
    path: '/reply',
    component: Layout,
    redirect: '/reply/list',
    name: 'Reply',
    meta: {
      title: t('router.replyManage'),
      icon: 'vi-eos-icons:role-binding'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Reply/List/List.vue'),
        name: 'ReplyList',
        meta: {
          title: t('router.replyList')
        }
      },
      {
        path: 'pendingreview',
        component: () => import('@/views/Reply/PendingReview/PendingReview.vue'),
        name: 'ReplyPendingReview',
        meta: {
          title: t('router.pendingReview')
        }
      },
      {
        path: 'rejected',
        component: () => import('@/views/Reply/Rejected/Rejected.vue'),
        name: 'ReplyRejected',
        meta: {
          title: t('router.rejected')
        }
      }
    ]
  },
  {
    path: '/topic',
    component: Layout,
    redirect: '/topic/list',
    name: 'Topic',
    meta: {
      title: t('router.topicManage'),
      icon: 'vi-eos-icons:role-binding'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Topic/List/List.vue'),
        name: 'TopicList',
        meta: {
          title: t('router.topicList')
        }
      },
      {
        path: 'pendingreview',
        component: () => import('@/views/Topic/PendingReview/PendingReview.vue'),
        name: 'TopicPendingReview',
        meta: {
          title: t('router.pendingReview')
        }
      },
      {
        path: 'rejected',
        component: () => import('@/views/Topic/Rejected/Rejected.vue'),
        name: 'TopicRejected',
        meta: {
          title: t('router.rejected')
        }
      }
    ]
  },
  {
    path: '/channel',
    component: Layout,
    redirect: '/channel/list',
    name: 'Channel',
    meta: {
      title: t('router.channelManage'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Channel/List/List.vue'),
        name: 'ChannelList',
        meta: {
          title: t('router.channelList')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
